import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'

export default function DigitalSystemsPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-systems', breadcrumbLabels)

  useSEO({
    title: 'Ticketing & Applications | Digital Systems for Music Organizations',
    description:
      'Automated digital systems for ticketing, applications, donations, and audience management. Stop losing audiences to spreadsheet chaos.',
    url: '/services/digital-systems',
    canonical: '/services/digital-systems',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Digital Systems for Music Organizations',
      description:
        'Custom digital systems for ticketing, applications, donations, and audience management for classical music organizations.',
      url: '/services/digital-systems',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useScrollReveal<HTMLDivElement>('.problem-item', { stagger: 0.08 })
  const packagesRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.12 })
  const stepsRef = useScrollReveal<HTMLDivElement>('.step-card', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Stop losing audiences to spreadsheet chaos.</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization has 200 email inquiries. 85 people started buying tickets. But only 42 completed checkout.
          Where did everyone else go?
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Most organizations have no idea. They track inquiries in one spreadsheet, ticket sales in another, and
          donations in a third. People fall through the cracks. Staff drowns in admin work.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          There&apos;s a better way. We build automated digital systems that track every step—from inquiry to ticket
          purchase, application to enrollment, or contact to donor—so you finally know what&apos;s working and
          what&apos;s broken.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Does This Sound Like Your Organization?</h2>
        <div ref={problemsRef} className="space-y-6">
          {[
            {
              problem: 'No Visibility Into Your Audience Journey',
              description:
                "You have no idea how many people inquired about tickets, how many started checkout, how many completed purchase. You're flying blind.",
            },
            {
              problem: 'Manual Tracking Across Multiple Disconnected Tools',
              description:
                'Inquiries in Gmail. Ticket sales in Eventbrite. Donations in PayPal. Member list in Excel. Spreadsheets everywhere. Nothing talks to each other.',
            },
            {
              problem: 'People Falling Through the Cracks',
              description:
                "Someone inquired about season tickets in September but never bought. Did you follow up? Did they lose interest? Did your email go to spam? You'll never know.",
            },
            {
              problem: 'Hours of Manual Data Entry Every Week',
              description:
                "Your staff spends 10-20 hours per week copying data between systems, sending follow-up emails manually, and trying to keep track of who's who.",
            },
            {
              problem: 'Confusing User Experience',
              description:
                'People have to fill out a form, then pay via a separate link, then confirm via another email, then receive tickets as a PDF attachment. Half of them give up.',
            },
            {
              problem: 'No Automated Follow-Ups',
              description:
                "When someone inquires, you manually send a follow-up. When they abandon checkout, you manually remind them. When applications are incomplete, you manually chase people down. It's exhausting.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-item border-destructive bg-destructive/5 space-y-2 border-l-4 py-4 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-6 text-center text-lg italic">
          The worst part? You have no data to improve. You don&apos;t know which marketing channels drive conversions,
          which email sequences work, or where people drop off. So you repeat the same chaos every season.
        </p>
      </section>

      {/* Solution Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">The Solution: Automated Digital Systems That Actually Work Together</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Imagine this: Someone visits your website. They submit an inquiry. Immediately, they get a welcome email.
            Three days later, they get a follow-up. They start buying tickets, and you see exactly where they are in
            checkout. They complete purchase, and you get notified. All in one system. No spreadsheets. No manual
            follow-ups. No lost opportunities.
          </p>
        </div>

        <div ref={packagesRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-8">
          <h3 className="text-center text-xl font-semibold">Digital Systems Packages</h3>
          <p className="text-muted-foreground text-center">
            Custom systems for ticketing, applications, donations, and audience management—priced for individuals and
            organizations.
          </p>

          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {/* Individual Systems */}
            <motion.div
              className="package-card border-border space-y-4 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div>
                <h4 className="text-lg font-semibold">Individual Systems</h4>
                <p className="text-primary mt-2 text-2xl font-bold">$2,500 - $5,000</p>
                <p className="text-muted-foreground mt-2 text-sm">For solo performers and private teachers</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Workshop registration system</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Simple booking/scheduling</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Payment integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Automated email notifications</span>
                </li>
              </ul>
              <p className="text-muted-foreground pt-2 text-xs">Timeline: 2-4 weeks</p>
            </motion.div>

            {/* Organization Systems */}
            <motion.div
              className="package-card border-primary relative space-y-4 rounded-lg border-2 p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                <span className="bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs font-semibold">
                  MOST POPULAR
                </span>
              </div>
              <div>
                <h4 className="text-lg font-semibold">Organization Systems</h4>
                <p className="text-primary mt-2 text-2xl font-bold">$2,500 - $10,000</p>
                <p className="text-muted-foreground mt-2 text-sm">For ensembles, festivals, and music organizations</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Festival application systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Ticketing & subscription management</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Donor portal & giving campaigns</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Member directory & login system</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>CRM integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Custom reporting dashboards</span>
                </li>
              </ul>
              <p className="text-muted-foreground pt-2 text-xs">Timeline: 4-8 weeks</p>
            </motion.div>
          </div>

          <div className="border-border mt-6 border-t pt-6">
            <p className="text-muted-foreground text-center text-sm">
              All systems include payment integration, automated emails, analytics dashboards, and training.
              <br />
              Pricing varies based on complexity and custom requirements.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Example: Ticketing System */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Example: How a Concert Ticketing System Works</h2>
        <div ref={stepsRef} className="space-y-6">
          {[
            {
              step: 'Step 1: Inquiry',
              description: 'Someone visits your website and fills out an interest form for your concert season.',
              automation:
                '→ Automated welcome email sent immediately with season information. → Contact added to CRM with "Inquiry" status. → Automated follow-up email sent 3 days later with ticket purchase link.',
            },
            {
              step: 'Step 2: Ticket Selection',
              description: 'Person clicks "Buy Tickets" and starts selecting seats/concerts.',
              automation:
                '→ CRM status updated to "Checkout Started." → You can see their cart in your admin dashboard. → If they don\'t complete checkout within 24 hours, automated reminder email sent.',
            },
            {
              step: 'Step 3: Purchase Completion',
              description: 'Person completes checkout and pays for tickets.',
              automation:
                '→ Payment automatically processed via Stripe/PayPal. → CRM status updated to "Ticket Holder." → Automated confirmation email sent with tickets and event details. → Notification sent to your team.',
            },
            {
              step: 'Step 4: Event Reminders',
              description: 'Concert date approaches.',
              automation:
                '→ Automated reminder email sent 1 week before concert. → Another reminder sent 1 day before with parking/arrival info. → Post-concert survey sent next day (optional).',
            },
            {
              step: 'Step 5: Re-Engagement',
              description: 'Season ends, time to sell next season.',
              automation:
                "→ Email campaign to previous ticket buyers with early-bird renewal offer. → Track who renews vs. who doesn't for next year's marketing.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="step-card border-border space-y-3 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  className="bg-primary text-primary-foreground flex h-10 w-10 items-center justify-center rounded-full font-bold"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                >
                  {idx + 1}
                </motion.div>
                <h3 className="text-lg font-semibold">{item.step}</h3>
              </div>
              <p className="text-muted-foreground pl-13">{item.description}</p>
              <div className="bg-primary/5 border-primary border-l-4 py-3 pl-4 text-sm">
                <p className="text-primary mb-2 font-semibold">What Happens Automatically:</p>
                <p className="text-muted-foreground whitespace-pre-line">{item.automation}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What This Means for Your Organization</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Reclaim 10-20 Hours Per Week',
              description:
                "No more manual follow-ups, no more copying data between spreadsheets, no more chasing down incomplete purchases. It's all automated.",
            },
            {
              benefit: 'Never Lose an Opportunity Again',
              description:
                'Every inquiry is tracked. Every abandoned cart gets a reminder. Every step is logged. No one falls through the cracks.',
            },
            {
              benefit: "See Exactly What's Working",
              description:
                'Track conversion rates at every step. Know which marketing channels drive sales. Optimize your systems year over year.',
            },
            {
              benefit: 'Reduce Friction for Your Audience',
              description:
                'People can inquire, purchase, register, or donate in one seamless process. No jumping between 5 different tools. Higher completion rates.',
            },
            {
              benefit: 'Professional User Experience',
              description:
                'Automated emails at every step make your organization look professional and organized. People feel supported throughout their journey.',
            },
            {
              benefit: 'Data to Improve Next Season',
              description:
                "After your season, you'll have exact data on inquiries, conversions, drop-off points, and completion rates. Use it to improve next year.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can this integrate with our existing website?',
              answer:
                'Yes. We can integrate the system with your current website, or we can build a new website as part of our Website Package and include the digital system.',
            },
            {
              question: 'What CRM do you recommend?',
              answer:
                'It depends on your needs and budget. For most organizations, we recommend Airtable (affordable, flexible, easy to use) or HubSpot (more robust, free tier available). We can also build a custom CRM if you have specific requirements.',
            },
            {
              question: 'Can we handle document uploads (for applications)?',
              answer:
                'Absolutely. We can set up secure document uploads for recommendations, recordings, resumes, repertoire lists—whatever your process requires.',
            },
            {
              question: 'What payment systems do you integrate with?',
              answer:
                'Stripe (most common), PayPal, Square, or custom solutions. We can also integrate with existing payment processors or ticketing platforms if you already have one.',
            },
            {
              question: 'How do we manage our audience data after launch?',
              answer:
                'We build an admin dashboard where you can view all contacts, track their status, segment audiences, export data, and send campaigns—all from one place.',
            },
            {
              question: 'What if our process is complex (multiple ticket types, member discounts, etc.)?',
              answer:
                "We can handle complex workflows: tiered pricing, member discounts, group sales, donation add-ons, multi-step applications, conditional logic, etc. We'll design the system to match your exact needs.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Streamline Your Digital Systems?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute system audit. We&apos;ll review your current processes, identify where people are
            dropping off, and show you exactly how an automated system would work for your organization.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No obligation. Just honest feedback on your systems and clear next steps if you want our help.
          </p>
        </div>
      </section>
    </div>
  )
}
